import { useEffect, useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  EditButton,
  Edit,
  SimpleForm,
  TextInput,
  Create,
  required,
  BooleanField,
  BooleanInput,
  DateField,
  SelectInput,
  Show,
  TabbedShowLayout,
  Tab,
  SearchInput,
  FunctionField,
  useRecordContext,
  useNotify,
  TabbedForm,
  FormTab,
  SelectArrayInput,
  PasswordInput,
} from "react-admin";
import {
  Box,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  Paper,
  Typography,
  Button,
  Divider,
  TextField as MUITextField,
  InputAdornment,
} from "@mui/material";
import { LockReset } from "@mui/icons-material";
import { RoleManager } from "../../components/RoleManager";
import { apiUrl } from "../../httpClient";
import { handleApiError } from "../../utils/errorHandler";
import type {
  CreateUserMarkupDto,
  UserMarkupResponseDto,
} from "../../types/api";

// Custom components for enhanced UX
const UserStatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  const getStatusColor = (user: Record<string, unknown>) => {
    if (!user.emailVerified) return "error";
    if (!user.onboardingCompleted) return "warning";
    return "success";
  };

  const getStatusText = (user: Record<string, unknown>) => {
    if (!user.emailVerified) return "Unverified";
    if (!user.onboardingCompleted) return "Incomplete";
    return "Active";
  };

  return (
    <Chip
      label={getStatusText(record)}
      color={getStatusColor(record)}
      size="small"
    />
  );
};

const RoleChips = () => {
  const record = useRecordContext();
  if (!record?.roles) return null;

  return (
    <Stack direction="row" spacing={0.5}>
      {(record.roles as Record<string, unknown>[]).map(
        (roleObj: Record<string, unknown>, index: number) => (
          <Chip
            key={index}
            label={roleObj.role || roleObj}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      )}
    </Stack>
  );
};

// Password Reset Button
const PasswordResetButton = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const handleResetPassword = async () => {
    if (!record?.id) return;

    try {
      const response = await fetch(
        apiUrl(`/users/${record.id}/reset-password`),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        notify("Password reset email sent successfully", { type: "success" });
      } else {
        notify("Failed to send password reset email", { type: "error" });
      }
    } catch {
      notify("Error sending password reset email", { type: "error" });
    }
  };

  return (
    <Tooltip title="Send password reset email">
      <IconButton onClick={handleResetPassword} color="warning">
        <LockReset />
      </IconButton>
    </Tooltip>
  );
};

// User Markup Panel
const UserMarkupPanel = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [markup, setMarkup] = useState<UserMarkupResponseDto | null>(null);
  const [form, setForm] = useState<{
    userId: string;
    userMarkupInput: string;
    startDate: string;
    endDate: string;
  }>({
    userId: "",
    userMarkupInput: "",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    if (!record?.id) return;
    loadMarkup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const loadMarkup = async () => {
    if (!record?.id) return;
    setLoading(true);
    try {
      const response = await fetch(apiUrl(`/user-markup/${record.id}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
        },
      });

      if (response.ok) {
        const data = (await response.json()) as UserMarkupResponseDto;
        setMarkup(data);
        const toDateInput = (value?: string | null) =>
          value ? value.slice(0, 10) : "";

        setForm({
          userId: record.id,
          userMarkupInput:
            data.userMarkup !== null && data.userMarkup !== undefined
              ? String(data.userMarkup * 100)
              : "",
          startDate: toDateInput(data.markupStartDate),
          endDate: toDateInput(data.markupEndDate),
        });
      } else if (response.status === 404) {
        setMarkup(null);
        setForm({
          userId: record.id,
          userMarkupInput: "",
          startDate: "",
          endDate: "",
        });
      } else {
        throw new Error("Failed to load user markup");
      }
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!record?.id) return;
    setSaving(true);
    try {
      const inputValue = form.userMarkupInput.trim();
      const numericValue = inputValue === "" ? NaN : Number(inputValue);
      if (
        Number.isNaN(numericValue) ||
        numericValue < 0 ||
        numericValue > 100
      ) {
        notify("Markup must be a number between 0 and 100", { type: "error" });
        setSaving(false);
        return;
      }

      const payload: CreateUserMarkupDto = {
        userId: record.id,
        userMarkup: numericValue / 100,
        startDate: form.startDate,
        endDate: form.endDate,
      };

      const response = await fetch(apiUrl("/user-markup"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to save user markup");

      notify("User markup saved", { type: "success" });
      loadMarkup();
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!record?.id) return;
    if (!confirm("Remove user-specific markup?")) return;

    try {
      const response = await fetch(apiUrl(`/user-markup/${record.id}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
        },
      });

      if (!response.ok) throw new Error("Failed to clear user markup");

      notify("User markup cleared", { type: "success" });
      loadMarkup();
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    }
  };

  if (!record) return null;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">User Markup</Typography>
        <Typography variant="body2" color="text.secondary">
          Set a user-specific markup (decimal). Example: 0.06 = 6%
        </Typography>

        {loading ? (
          <Typography>Loading markup…</Typography>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={markup?.isActive ? "Active" : "Inactive"}
                color={markup?.isActive ? "success" : "default"}
                size="small"
              />
              <Typography variant="body2">
                Current:{" "}
                {markup?.userMarkup !== null && markup?.userMarkup !== undefined
                  ? `${(markup.userMarkup * 100).toFixed(2)}%`
                  : "—"}
              </Typography>
            </Stack>

            <Divider />

            <Stack spacing={2}>
              <MUITextField
                label="Markup (%)"
                type="text"
                inputMode="decimal"
                value={form.userMarkupInput}
                onChange={(e) => {
                  const v = e.target.value;
                  // allow digits + optional single dot
                  if (/^\d*\.?\d*$/.test(v)) {
                    setForm({ ...form, userMarkupInput: v });
                  }
                }}
                sx={{ maxWidth: 240 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {form.userMarkupInput === ""
                        ? "%"
                        : `${form.userMarkupInput}%`}
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" spacing={1} alignItems="center">
                <MUITextField
                  label="Start Date"
                  type="date"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <Typography variant="body2" color="text.secondary">
                  to
                </Typography>
                <MUITextField
                  label="End Date"
                  type="date"
                  value={form.endDate}
                  onChange={(e) =>
                    setForm({ ...form, endDate: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Markup"}
              </Button>
              <Button variant="outlined" color="error" onClick={handleClear}>
                Clear Markup
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};

// User List Component
export const UserList = () => (
  <List
    filters={[
      <SearchInput
        key="search"
        source="search"
        placeholder="Search by name or email"
        alwaysOn
      />,
      <SelectInput
        key="role"
        source="role"
        choices={[
          { id: "superAdmin", name: "Super Admin" },
          { id: "admin", name: "Admin" },
          { id: "support", name: "Support" },
          { id: "regular", name: "Regular" },
        ]}
        alwaysOn
      />,
      <SelectInput
        key="onboardingCompleted"
        source="onboardingCompleted"
        choices={[
          { id: true, name: "Completed" },
          { id: false, name: "Incomplete" },
        ]}
      />,
    ]}
    sort={{ field: "createdAt", order: "DESC" }}
  >
    <Datagrid rowClick="show">
      <TextField source="username" label="Email" />
      <TextField source="name" />
      <TextField source="phoneNumber" label="Phone" />
      <TextField source="residency" />
      <RoleChips />
      <UserStatusChip />
      <BooleanField source="emailVerified" label="Verified" />
      <BooleanField source="onboardingCompleted" label="Onboarded" />
      <BooleanField source="useTestCredentials" label="Test User" />
      <DateField source="createdAt" label="Created" showTime />
      <EditButton />
    </Datagrid>
  </List>
);

// User Show Component
// useRecordContext() only resolves inside <Show>'s children — it must live in a
// component rendered *within* the TabbedShowLayout, not in UserShow itself.
const UserActionsTabContent = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
      <RoleManager record={record} resource="users" />
      <PasswordResetButton />
    </Box>
  );
};

export const UserShow = () => {
  return (
    <Show>
      <TabbedShowLayout>
        <Tab label="Personal Information">
          <TextField source="id" />
          <TextField source="name" />
          <TextField source="username" label="Email" />
          <TextField source="phoneNumber" label="Phone" />
          <TextField source="residency" />
          <TextField source="avatarUrl" label="Avatar URL" />
        </Tab>

        <Tab label="Account Status">
          <FunctionField
            label="Roles"
            render={(record) => (
              <Stack direction="row" spacing={1}>
                {(record?.roles as Record<string, unknown>[])?.map(
                  (roleObj: Record<string, unknown>, index: number) => (
                    <Chip
                      key={index}
                      label={roleObj.role || roleObj}
                      color="primary"
                      variant="outlined"
                    />
                  ),
                )}
              </Stack>
            )}
          />
          <BooleanField source="emailVerified" label="Email Verified" />
          <BooleanField
            source="onboardingCompleted"
            label="Onboarding Completed"
          />
          <BooleanField source="useTestCredentials" label="Test User" />
          <BooleanField source="isOAuthAccount" label="OAuth Account" />
        </Tab>

        <Tab label="System Information">
          <DateField source="createdAt" label="Created At" showTime />
          <DateField source="updatedAt" label="Updated At" showTime />
        </Tab>

        <Tab label="Markup">
          <UserMarkupPanel />
        </Tab>

        <Tab label="Actions">
          <UserActionsTabContent />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

// User Edit Component
export const UserEdit = () => (
  <Edit>
    <TabbedForm>
      <FormTab label="Profile">
        <TextInput source="firstName" validate={required()} />
        <TextInput source="lastName" validate={required()} />
        <TextInput source="phoneNumber" label="Phone Number" />
        <TextInput source="residency" label="Country Code (e.g., US, CA)" />
        <BooleanInput
          source="onboardingCompleted"
          label="Onboarding Completed"
        />
        <BooleanInput
          source="useTestCredentials"
          label="Use Test Credentials"
          helperText="Enable this user to authenticate with test credentials"
        />
      </FormTab>
    </TabbedForm>
  </Edit>
);
// Roles are deliberately not editable here — role changes are authority-checked
// and audited separately via the "Manage Roles" action (RoleManager) on the Show page,
// which calls the dedicated PUT /admin/users/:id/role endpoint.

// User Create Component
export const UserCreate = () => (
  <Create>
    <SimpleForm>
      <TextInput
        source="username"
        label="Email"
        type="email"
        validate={required()}
      />
      <PasswordInput source="password" validate={required()} />
      <TextInput source="firstName" validate={required()} />
      <TextInput source="lastName" validate={required()} />
      <TextInput source="phoneNumber" label="Phone Number" />
      <TextInput
        source="residency"
        label="Country Code (e.g., US, CA)"
        validate={required()}
      />

      <SelectArrayInput
        source="roles"
        label="User Roles"
        validate={required()}
        choices={[
          { id: "superAdmin", name: "Super Admin" },
          { id: "admin", name: "Admin" },
          { id: "support", name: "Support" },
          { id: "regular", name: "Regular" },
        ]}
      />
      {/* Admin-created accounts are always created with a verified email by the API. */}
    </SimpleForm>
  </Create>
);
